/**
 * §A.6 · Los tipos de consentimiento.
 *
 * ## Cambiar el texto **no modifica lo ya firmado**, y es la pantalla entera
 *
 * H-8, literal: «un `PATCH` que reescriba el texto dejaría 3.000 firmas apuntando a
 * un texto que nadie leyó». Cada versión publicada se guarda entera y las firmas
 * siguen apuntando a la suya.
 *
 * Eso convierte esta pantalla en algo distinto de un formulario de edición: **cada
 * guardado publica una versión**, y hay que decirlo antes de pulsar, con la cifra de
 * a cuántas familias va a afectar. Quien cambia una coma en el texto del
 * consentimiento de fotos está a punto de poner a tres mil familias a firmar otra vez.
 *
 * ## Menos `sort_order`, que no publica nada
 *
 * «El orden en la pantalla no es el documento, y publicar una versión por reordenar
 * una lista pondría a tres mil familias a firmar otra vez lo mismo.» Así que el aviso
 * aparece solo cuando cambia algo del documento —lo decide `publicaVersion`—.
 *
 * ## `outdated_signatures` no es un error
 *
 * «Son familias que firmaron el texto que había, y ese texto sigue existiendo. Es la
 * cifra que dice a cuántas hay que volver a pedírselo.» Se pinta sin color de alarma.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { cuenta, fecha } from '@/shared/ui/formato';
import {
  publicaVersion,
  type TipoDeConsentimiento,
} from '@/backoffice/tiposDeCatalogos';

export function CatalogoDeConsentimientos() {
  const cache = useQueryClient();
  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<TipoDeConsentimiento | null>(null);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [texto, setTexto] = useState('');
  const [obligatorio, setObligatorio] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);

  const tipos = useQuery({
    queryKey: ['admin', 'consentimientos'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin['consent-types'].get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as TipoDeConsentimiento[];
    },
  });

  const guardar = useMutation({
    mutationFn: async () => {
      const comun = {
        label: label.trim(),
        description: texto.trim() === '' ? null : texto.trim(),
        is_required: obligatorio,
      };
      if (editando) {
        // El identificador **es el código**, no un uuid.
        const r = await api.v1.admin['consent-types']({
          id: editando.code,
        }).patch(comun);
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return r.data;
      }
      const r = await api.v1.admin['consent-types'].post({
        ...comun,
        code: code.trim(),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setCreando(false);
      setEditando(null);
      setCode('');
      setLabel('');
      setTexto('');
      setObligatorio(false);
      void cache.invalidateQueries({ queryKey: ['admin', 'consentimientos'] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (tipos.isPending) return <Cargando filas={5} etiqueta="Cargando" />;

  if (tipos.isError || !tipos.data) {
    return (
      <PanelError
        problema={aProblema(tipos.error)}
        reintentar={() => void tipos.refetch()}
      />
    );
  }

  if (creando || editando) {
    const vaAPublicar =
      editando !== null &&
      publicaVersion(editando, {
        label: label.trim(),
        description: texto.trim() === '' ? null : texto.trim(),
        is_required: obligatorio,
      });

    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          guardar.mutate();
        }}
      >
        <div className="titulo-fila">
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setCreando(false);
              setEditando(null);
              setProblema(null);
            }}
          >
            ← Consentimientos
          </button>
        </div>

        <h1>
          {editando
            ? `Editar «${editando.label}»`
            : 'Un tipo de consentimiento más'}
        </h1>

        {/*
          El aviso que da sentido a la pantalla, y **solo cuando va a pasar**: si solo
          se ha tocado el orden, no se publica nada y decirlo sería el cuento del lobo.
        */}
        {vaAPublicar && (
          <div className="aviso aviso-grave" role="alert">
            <p>
              <strong>Esto publica una versión nueva del documento.</strong> Lo
              ya firmado no se toca: las firmas siguen apuntando al texto que
              esas familias leyeron, y ese texto se queda guardado.
            </p>
            <p className="tenue">
              Lo que cambia es lo que se pide a partir de ahora.{' '}
              {editando.signatures > 0 && (
                <>
                  Hay {cuenta(editando.signatures, 'firma', 'firmas')} de la
                  versión {editando.current_version}, y a esas familias habrá
                  que volver a pedírselo.
                </>
              )}
            </p>
          </div>
        )}

        <div className="rejilla">
          {!editando && (
            <label>
              Código
              <input
                required
                maxLength={40}
                pattern="[a-z0-9_]+"
                placeholder="imagen_menores"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
          )}

          <label>
            Cómo se llama
            <input
              required
              maxLength={200}
              placeholder="Uso de imagen"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </label>

          <label>
            Hace falta para entrenar
            <select
              value={obligatorio ? 'si' : 'no'}
              onChange={(e) => setObligatorio(e.target.value === 'si')}
            >
              <option value="no">No, es opcional</option>
              <option value="si">Sí, es obligatorio</option>
            </select>
          </label>
        </div>

        <label>
          El texto que firma la familia
          <textarea
            rows={8}
            maxLength={4000}
            placeholder="Autorizo que se publiquen fotografías de mi hijo o hija en las redes del club…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>

        <p className="tenue">
          {obligatorio ? (
            <strong>
              Un consentimiento obligatorio bloquea la matrícula del alumno
              hasta que se firma. Para las fotos eso no vale: una familia puede
              decir que no y el niño entrena igual.
            </strong>
          ) : (
            'Un consentimiento opcional se pide, y si la familia dice que no, no pasa nada más.'
          )}
        </p>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending
              ? 'Guardando…'
              : vaAPublicar
                ? 'Publicar la versión nueva'
                : 'Guardar'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => {
              setCreando(false);
              setEditando(null);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  return (
    <section>
      <div className="titulo-fila">
        <h1>Tipos de consentimiento</h1>
        <button type="button" onClick={() => setCreando(true)}>
          Uno más
        </button>
      </div>

      <p className="tenue">
        Lo que se le pide firmar a las familias en los doscientos clubes.{' '}
        <strong>Cambiar un texto publica una versión nueva</strong> y no toca
        nada de lo ya firmado.
      </p>

      {problema && <PanelError problema={problema} />}

      <ul className="papeles">
        {tipos.data.map((t) => (
          <li key={t.code}>
            <div className="aviso-cabeza">
              <strong>{t.label}</strong>
              {t.is_required && (
                <span className="pastilla pastilla-left">Obligatorio</span>
              )}
              <span className="pastilla pastilla-left">
                Versión {t.current_version}
              </span>
              <code className="tenue">{t.code}</code>
            </div>

            {t.description && <p>{t.description}</p>}

            <p className="tenue">
              {t.signatures === 0
                ? 'Nadie lo ha firmado todavía'
                : `${cuenta(t.signatures, 'firma', 'firmas')} vivas`}
              {t.outdated_signatures > 0 && (
                <>
                  {' · '}
                  {/*
                    Sin color de alarma: no es un error, son familias que firmaron
                    el texto que había. Es la cifra que dice a cuántas volver a
                    pedírselo.
                  */}
                  {cuenta(t.outdated_signatures, 'firma', 'firmas')} de una
                  versión anterior
                </>
              )}
              {t.versions.length > 1 &&
                ` · ${cuenta(t.versions.length, 'versión publicada', 'versiones publicadas')}`}
            </p>

            {t.versions.length > 1 && (
              <ul className="lista-clara">
                {t.versions.map((v) => (
                  <li key={v.version}>
                    <span className="tenue">
                      v{v.version} · {fecha(v.published_at)} ·{' '}
                      {cuenta(v.signatures, 'firma', 'firmas')}
                    </span>{' '}
                    {v.label}
                  </li>
                ))}
              </ul>
            )}

            <div className="aviso-pie">
              <span className="tenue">Orden: {t.sort_order}</span>
              <button
                type="button"
                className="btn-plano"
                onClick={() => {
                  setLabel(t.label);
                  setTexto(t.description ?? '');
                  setObligatorio(t.is_required);
                  setEditando(t);
                  setProblema(null);
                }}
              >
                Editar el texto
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
